export type ElementCategory = '자연' | '생명' | '문명' | '음식' | '개념' | '현대' | '신화' | '우주' | '예술' | '기타';

export interface ElementInfo {
  name: string;
  icon: string;
  imageUrl?: string;
  description?: string;
  tier: number;
  category: ElementCategory;
  origin: 'base' | 'known' | 'generated' | 'milestone';
  depth?: number;
  discoveredCount?: number;
}

export interface MilestoneDefinition {
  threshold: number;
  name: string;
  description: string;
  icon: string;
  imageUrl: string;
  tier: number;
  category: ElementCategory;
}

export const MILESTONES: readonly MilestoneDefinition[] = [
  { threshold: 80, name: '뜨거움', description: "'뜨거움'을 깨우쳤어요!", icon: '🔥', imageUrl: '/elements/hotness.webp', tier: 3, category: '개념' },
  { threshold: 250, name: '차가움', description: "'차가움'을 깨우쳤어요!", icon: '❄️', imageUrl: '/elements/coldness.webp', tier: 4, category: '개념' },
  { threshold: 500, name: '건조함', description: "'건조함'을 깨우쳤어요!", icon: '🏜️', imageUrl: '/elements/dryness.webp', tier: 5, category: '개념' },
  { threshold: 800, name: '습함', description: "'습함'을 깨우쳤어요!", icon: '💧', imageUrl: '/elements/humidity.webp', tier: 6, category: '개념' },
];

export function getMilestoneElement(milestone: MilestoneDefinition): ElementInfo {
  return {
    name: milestone.name,
    icon: milestone.icon,
    imageUrl: milestone.imageUrl,
    tier: milestone.tier,
    category: milestone.category,
    origin: 'milestone',
  };
}

export function getNextMilestone(count: number): MilestoneDefinition | null {
  return MILESTONES.find((m) => count < m.threshold) ?? null;
}

export function getEarnedMilestones(count: number): MilestoneDefinition[] {
  return MILESTONES.filter((m) => count >= m.threshold);
}

export interface KnownRecipe {
  a: string;
  b: string;
  result: Omit<ElementInfo, 'origin'>;
}

export const BASE_ELEMENTS: readonly ElementInfo[] = [
  { name: '불', icon: '🔥', imageUrl: '/elements/fire.webp', tier: 0, category: '자연', origin: 'base', description: '모든 것을 태우고, 모든 것을 밝히는 태초의 힘.' },
  { name: '물', icon: '💧', imageUrl: '/elements/water.webp', tier: 0, category: '자연', origin: 'base', description: '낮은 곳으로 흘러 세상을 적시는 겸손한 원소.' },
  { name: '흙', icon: '🌍', imageUrl: '/elements/earth.webp', tier: 0, category: '자연', origin: 'base', description: '만물을 품고 기다리는 대지의 인내심.' },
  { name: '바람', icon: '💨', imageUrl: '/elements/wind.webp', tier: 0, category: '자연', origin: 'base', description: '보이지 않지만 모든 곳에 닿는 자유로운 존재.' }
];

const KNOWN_RECIPE_LIST: readonly KnownRecipe[] = [
  { a: '물', b: '불', result: { name: '증기', icon: '☁️', tier: 1, category: '자연', description: '물과 불이 만나 하늘로 오르는 숨결.' } },
  { a: '물', b: '흙', result: { name: '진흙', icon: '🟫', tier: 1, category: '자연', description: '대지가 물을 머금어 부드러워진 살갗.' } },
  { a: '불', b: '흙', result: { name: '용암', icon: '🌋', tier: 1, category: '자연', description: '땅속 깊이 잠든 불의 피가 흐른다.' } },
  { a: '물', b: '바람', result: { name: '비', icon: '🌧️', tier: 2, category: '자연', description: '하늘이 대지를 그리워해 흘리는 눈물.' } },
  { a: '불', b: '바람', result: { name: '연기', icon: '💨', tier: 1, category: '자연', description: '불꽃이 바람에 실려 남긴 마지막 인사.' } },
  { a: '증기', b: '바람', result: { name: '태풍', icon: '🌪️', tier: 3, category: '자연', description: '하늘의 분노가 소용돌이치는 순간.' } },
  { a: '용암', b: '물', result: { name: '돌', icon: '🪨', tier: 2, category: '자연', description: '뜨거운 것이 식으면 영원이 된다.' } },
  { a: '진흙', b: '불', result: { name: '벽돌', icon: '🧱', tier: 2, category: '문명', description: '흙의 인내가 불의 시련을 견뎌낸 증거.' } },
  { a: '돌', b: '불', result: { name: '유리', icon: '🧪', tier: 2, category: '문명', description: '단단한 것이 녹으면 투명해진다.' } }
];

const GENERATED_NAMES: readonly string[] = [
  '기운', '파동', '결정', '정령', '회오', '빛살', '안개', '노을', '울림', '잔향',
  '물결', '유성', '도약', '축복', '신전', '광휘', '서리', '섬광', '심연', '창공',
  '여운', '온기', '울타', '별빛', '새벽', '황혼', '파편', '원석', '수맥', '정화',
  '근원', '진동', '축성', '진화', '맥동', '융합', '환류', '신호', '결속', '변환',
  '윤회', '환상', '미광', '청명', '폭풍', '씨앗', '메아', '공명', '유대', '생장',
  '격류', '무늬', '도시', '마을', '회로', '강철', '소용', '기억', '추억', '도감',
  '연결', '서사', '운명', '별무', '물빛', '불꽃', '황금', '심장', '정수', '희망'
];

export const CATEGORY_ICONS: Record<ElementCategory, string> = {
  자연: '🌿',
  생명: '🧬',
  문명: '🏛️',
  음식: '🍽️',
  개념: '💡',
  현대: '📱',
  신화: '🐉',
  우주: '🌌',
  예술: '🎨',
  기타: '✨'
};

const CATEGORIES: readonly ElementCategory[] = ['자연', '생명', '문명', '음식', '개념', '현대', '신화', '우주', '예술', '기타'];

export function toComboKey(a: string, b: string): string {
  return [a, b].sort((left, right) => left.localeCompare(right, 'ko-KR')).join('+');
}

export const KNOWN_RECIPES_BY_KEY: Readonly<Record<string, ElementInfo>> = Object.freeze(
  KNOWN_RECIPE_LIST.reduce<Record<string, ElementInfo>>((acc, recipe) => {
    acc[toComboKey(recipe.a, recipe.b)] = { ...recipe.result, origin: 'known' };
    return acc;
  }, {})
);

const KNOWN_RESULT_ICONS: Readonly<Record<string, string>> = Object.freeze(
  KNOWN_RECIPE_LIST.reduce<Record<string, string>>((acc, recipe) => {
    acc[recipe.result.name] = recipe.result.icon;
    return acc;
  }, {})
);

export function createBaseElementMap(): Record<string, ElementInfo> {
  return BASE_ELEMENTS.reduce<Record<string, ElementInfo>>((acc, element) => {
    acc[element.name] = { ...element };
    return acc;
  }, {});
}

function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash;
}

export function createGeneratedElement(
  a: ElementInfo,
  b: ElementInfo,
  existingNames: ReadonlySet<string>
): ElementInfo {
  const key = toComboKey(a.name, b.name);
  const seed = hashString(key);
  const maxTier = Math.max(a.tier, b.tier);
  const tier = Math.min(6, maxTier + (seed % 2));
  const category = CATEGORIES[seed % CATEGORIES.length];

  let index = seed % GENERATED_NAMES.length;
  let attempts = 0;
  let resultName = GENERATED_NAMES[index];

  while (existingNames.has(resultName) && attempts < GENERATED_NAMES.length) {
    index = (index + 17) % GENERATED_NAMES.length;
    resultName = GENERATED_NAMES[index];
    attempts += 1;
  }

  if (existingNames.has(resultName)) {
    resultName = '신조';
  }

  return {
    name: resultName,
    icon: CATEGORY_ICONS[category],
    tier,
    category,
    origin: 'generated'
  };
}

export function resolveElementIcon(name: string, category: ElementCategory): string {
  return KNOWN_RESULT_ICONS[name] ?? CATEGORY_ICONS[category] ?? '✨';
}
